import { withSuspense } from '@/ui/utils/withSuspense'
import { RequireKeys } from '@/utils/types'

import { ActionsSkeleton } from './components/skeleton/ActionsSkeleton'
import { stringifyObjectivesToStableActions } from './logic/stringifyObjectives'
import { InjectedActionsContext, Objective } from './logic/types'
import { useActionHandlers } from './logic/useActionHandlers'
import { useSettingsDialog } from './settings-dialog/logic/useSettingsDialog'
import { ActionsView } from './views/ActionsView'
import { useEffect } from 'react'

export interface ActionsContainerProps {
  objectives: Objective[]
  context?: InjectedActionsContext
  onFinish?: () => void // called only once, after render when all actions are marked successful
  variant?: 'default' | 'dialog'
  enabled?: boolean
  setActionsInProgress?: (actionsInProgress: boolean) => void
}

function ActionsContainer({
  objectives,
  context,
  onFinish,
  variant = 'default',
  enabled,
  setActionsInProgress,
}: RequireKeys<ActionsContainerProps, 'enabled'>) {
  const { handlers, settingsDisabled, actionsInProgress } = useActionHandlers(objectives, {
    context,
    enabled,
    onFinish,
  })
  const settingsDialogProps = useSettingsDialog()

  useEffect(() => {
    setActionsInProgress?.(actionsInProgress)
  }, [actionsInProgress, setActionsInProgress])

  return (
    <ActionsView
      variant={variant}
      actionHandlers={handlers}
      settingsDisabled={settingsDisabled}
      settingsDialogProps={settingsDialogProps}
    />
  )
}

// @note: rerenders ActionsContainer when actions change. This is needed to not break the rule of hooks
function ActionsContainerWithKey(props: ActionsContainerProps) {
  return (
    <ActionsContainer
      {...props}
      key={stringifyObjectivesToStableActions(props.objectives)}
      enabled={props.enabled ?? true}
    />
  )
}
const ActionsContainerWithSuspense = withSuspense(ActionsContainerWithKey, ActionsSkeleton)
export { ActionsContainerWithSuspense as ActionsContainer }
